/* __next_internal_client_entry_do_not_use__ Default,accordionItemTailwindVariants auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
// Global
import { Text } from '@sitecore-content-sdk/nextjs';
import { useCallback, useRef } from 'react';
import { tv } from 'tailwind-variants';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { SvgIcon } from 'helpers/SvgIcon';
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { useAccordionItemContext } from 'helpers/Context/AccordionContext';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const AccordionItem = (props)=>{
    const isEditing = useIsEditing();
    const { content, heading } = props?.fields || {};
    const { uid } = props?.rendering || {};
    const accordionId = `accordion-item-${uid}`;
    /*
   * REFS
   */ const accordionItemButtonRef = useRef(null);
    const accordionItemBaseRef = useRef(null);
    const accordionItemBodyRef = useRef(null);
    /*
   * State
   */ const { isOpening, toggleOpen, isVisible, scrollToOpenPanel } = useAccordionItemContext(uid ?? '', accordionItemBodyRef);
    /*
   * Convenience Methods
   */ const toggleAccordion = useCallback(()=>{
        toggleOpen();
    }, [
        toggleOpen
    ]);
    /*
   * Lifecycle
   */ const scrollCondition = scrollToOpenPanel && isOpening;
    useScrollElementIntoView(accordionItemButtonRef.current, {
        stickyHeaderId: 'header',
        condition: scrollCondition,
        animationElement: accordionItemBodyRef.current,
        scrollTargetId: `${accordionId}-scroll-target`
    });
    /*
   * Rendering
   */ const { base, wrapper, buttonWrapper, iconWrapper, contentContainer, contentContainerInner, richTextWrapper, headingText } = accordionItemTailwindVariants({
        isOpening,
        isVisible
    });
    return /*#__PURE__*/ _createElement("div", {
        ref: accordionItemBaseRef,
        className: base(),
        "data-component": "authorable/shared/lists/accordionitem",
        ...editingHydrationProps(isEditing),
        key: accordionId,
        ...getTestProps(`accordion-item-${accordionId}`)
    }, /*#__PURE__*/ _jsxs("div", {
        className: wrapper(),
        children: [
            /*#__PURE__*/ _jsxs("button", {
                id: accordionId,
                "aria-expanded": isOpening,
                "aria-controls": `${accordionId}-content`,
                "aria-label": heading?.value,
                className: buttonWrapper(),
                onClick: toggleAccordion,
                ref: accordionItemButtonRef,
                type: "button",
                ...getTestProps(`toggle-button`),
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        field: heading,
                        className: headingText(),
                        tag: "h3",
                        ...getTestProps(`heading`)
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: iconWrapper(),
                        ...getTestProps(`icon`),
                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                            fill: "none",
                            icon: "chevron-down",
                            size: "xs",
                            viewBox: "0 0 24 24"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                id: `${accordionId}-content`,
                "aria-labelledby": accordionId,
                className: contentContainer(),
                role: "region",
                ref: accordionItemBodyRef,
                tabIndex: isOpening ? undefined : -1,
                "aria-hidden": !isOpening,
                children: /*#__PURE__*/ _jsx("div", {
                    className: contentContainerInner(),
                    children: /*#__PURE__*/ _jsx(RichTextWrapper, {
                        className: richTextWrapper(),
                        field: content,
                        tabIndex: -1,
                        ...getTestProps(`content`)
                    })
                })
            })
        ]
    }));
};
export const Default = withStandardComponentWrapper(AccordionItem);
export const accordionItemTailwindVariants = tv({
    defaultVariants: {
        isOpen: false,
        isVisible: true
    },
    slots: {
        base: [
            'border',
            'border-component-accordion-item-border',
            'hover:border-component-accordion-item-border-hover',
            'rounded-component-accordion-item-border-radius',
            'focus-within:shadow-[0_0_0_3px_var(--color-core-color-4-SkyBlue)]',
            'break-inside-avoid'
        ],
        wrapper: [
            'flex',
            'flex-col',
            'gap'
        ],
        buttonWrapper: [
            'cursor-pointer',
            'duration-300',
            'flex',
            'items-center',
            'justify-between',
            'px-component-accordion-item-padding-x',
            'py-component-accordion-item-padding-y',
            'transition-none',
            'w-full',
            'scroll-mt-[123px]',
            'hover:underline',
            'bg-component-accordion-item-surface-active',
            'rounded-component-accordion-item-border-radius',
            'focus:outline-none',
            'group'
        ],
        contentContainer: [
            'duration-300',
            'ease-in-out',
            'grid',
            'overflow-hidden',
            'transition-all',
            'rounded-component-accordion-item-border-radius',
            'bg-component-accordion-item-surface-active'
        ],
        contentContainerInner: [
            'overflow-hidden'
        ],
        iconWrapper: [
            'transform',
            'transition-transform',
            'mx-spacing-spacing-4',
            'text-component-accordion-item-icon',
            'group-hover:text-component-accordion-item-icon-hover',
            'group-focus:text-component-accordion-item-icon-hover'
        ],
        richTextWrapper: [
            'text-color-text-text',
            'text-left',
            'font-normal',
            'mb-0',
            'px-component-accordion-item-padding-x',
            'pb-component-accordion-item-padding-y',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-normal',
            'leading-normal'
        ],
        headingText: [
            'text-color-text-text',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'font-bold',
            'text-left',
            'leading-typography-line-height-body-large'
        ]
    },
    variants: {
        isOpening: {
            false: {
                base: [
                    'rounded-component-accordion-item-border-radius'
                ],
                buttonWrapper: [
                    'rounded-component-accordion-item-border-radius'
                ],
                contentContainer: [
                    'grid-rows-[0fr]',
                    'opacity-0',
                    'pointer-events-none',
                    'select-none',
                    'invisible'
                ],
                iconWrapper: []
            },
            true: {
                base: [
                    'rounded-component-accordion-item-border-radius',
                    'border-component-accordion-item-border-active'
                ],
                buttonWrapper: [
                    'rounded-t-component-accordion-item-border-radius',
                    'pb-spacing-spacing-16'
                ],
                contentContainer: [
                    'grid-rows-[1fr]',
                    'opacity-100',
                    'visible'
                ],
                iconWrapper: [
                    'rotate-180'
                ]
            }
        },
        isVisible: {
            false: [
                'max-sm:hidden'
            ],
            true: []
        }
    }
});
